/** Rendering of decision-table findings: status, severity, legal citation, reasons and trace. */
import React from "react";
import { Collapse, Descriptions, Space, Table, Tag, Tooltip, Typography } from "antd";
import { CheckCircleFilled, CloseCircleFilled, ClockCircleOutlined } from "@ant-design/icons";
import type { Finding } from "./api";

const SEVERITY: Record<Finding["severity"], { color: string; label: string }> = {
  blocking: { color: "red", label: "Bloccante" },
  warning: { color: "orange", label: "Avviso" },
  advice: { color: "blue", label: "Consiglio" },
};

/** gdpr:28.3 -> "GDPR art. 28.3" */
export const cite = (r: string) => {
  const [act, art] = r.split(":");
  const name: Record<string, string> = { gdpr: "GDPR", nis2: "NIS2", dlgs81: "D.Lgs. 81/08", aml: "D.Lgs. 231/07", aiact: "AI Act" };
  return `${name[act] ?? act.toUpperCase()} art. ${art}`;
};

export const StatusTag: React.FC<{ f: Finding }> = ({ f }) => f.status === "met"
  ? <Tag icon={<CheckCircleFilled />} color="success">Soddisfatto</Tag>
  : <Tag icon={<CloseCircleFilled />} color={SEVERITY[f.severity].color}>{SEVERITY[f.severity].label}</Tag>;

export const FindingsTable: React.FC<{ findings: Finding[]; labels?: Record<string, string>; compact?: boolean }> = ({ findings, labels = {}, compact }) =>
  <Table<Finding>
    size="small"
    rowKey={(f) => `${f.table}|${f.obligation}|${f.subject.id}`}
    dataSource={findings}
    pagination={compact ? false : { pageSize: 20 }}
    scroll={{ x: 760 }}
    expandable={{
      expandedRowRender: (f) => <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="Regole applicate">{f.trace.rows.join(", ")}</Descriptions.Item>
        <Descriptions.Item label="Fatti valutati"><code style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(f.trace.facts, null, 1)}</code></Descriptions.Item>
        {f.reasons.length > 0 && <Descriptions.Item label="Cosa manca">{f.reasons.join("; ")}</Descriptions.Item>}
      </Descriptions>,
    }}
    columns={[
      { title: "Esito", width: 130, render: (_, f) => <StatusTag f={f} />, filters: [{ text: "Da sanare", value: "unmet" }, { text: "Soddisfatti", value: "met" }], onFilter: (v, f) => f.status === v },
      { title: "Obbligo", dataIndex: "obligation", render: (o: string) => <Typography.Text strong>{o.replace(/_/g, " ")}</Typography.Text> },
      { title: "Riferito a", render: (_, f) => <Space size={4}><Tag>{f.subject.type.replace(/_/g, " ")}</Tag>{labels[f.subject.id] ?? f.subject.id}</Space> },
      { title: "Norma", render: (_, f) => <Space size={2} wrap>{f.requirements.map((r) => <Tag key={r} color="geekblue">{cite(r)}</Tag>)}</Space> },
      { title: "Scadenza", width: 90, render: (_, f) => f.deadlineHours ? <Tag icon={<ClockCircleOutlined />}>{f.deadlineHours} h</Tag> : null },
      { title: "", width: 70, render: (_, f) => f.draft ? <Tooltip title="Tabella in bozza: citazioni da validare con DPO/RSPP"><Tag>bozza</Tag></Tooltip> : null },
    ]}
  />;

export const TraceNote: React.FC = () => <Collapse ghost items={[{ key: "1", label: "Come funziona", children:
  <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
    Ogni tabella decisionale classifica i fatti già inseriti (persone, fornitori, asset, incidenti…), ne ricava gli obblighi
    di legge e verifica se esistono le evidenze. Nessuna intelligenza artificiale: stesse risposte, stesso esito.
    Apri una riga per vedere quali regole hanno scattato e su quali fatti.
  </Typography.Paragraph> }]} />;
